import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import type WaypointCategoryValue from '@alltrails/amplitude/enums/WaypointCategoryValue';
import logCommunityWaypointsFiltersApplied from '@alltrails/amplitude/events/logCommunityWaypointsFiltersApplied';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationPathname } from '@alltrails/core/hooks/useClientNavigation';
import Checkbox from '@alltrails/denali/components/Checkbox';
import type { WaypointCategoryUid } from '@alltrails/types/Waypoint';
import { getWaypointCategoryConfig } from '@alltrails/waypoints/utils/waypointCategories';
import { useAppDispatch, useAppSelector } from '../../redux';
import { toggleCommunityWaypointCategory } from '../../redux/reducer';
import { getPage } from '../../utils/amplitudeHelpers';
import styles from './CommunityWaypointCategoryFilters.module.scss';

function getWaypointCategoryValue(uid: WaypointCategoryUid): WaypointCategoryValue {
  switch (uid) {
    case 'event-marker':
      return 'event_marker';
    default:
      return uid;
  }
}

export const COMMUNITY_WAYPOINT_FILTER_UIDS: WaypointCategoryUid[] = ['camping', 'water', 'navigation', 'landmark', 'event-marker'];

const CommunityWaypointCategoryFilters = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const pathname = useClientNavigationPathname();
  const selected = useAppSelector(state => state.map.communityWaypointCategories);
  const trailId = useAppSelector(state => state.map.trailId);

  const onToggle = useCallback(
    (uid: WaypointCategoryUid) => {
      const isAddition = !selected.includes(uid);
      const nextSelected = isAddition ? [...selected, uid] : selected.filter(u => u !== uid);
      dispatch(toggleCommunityWaypointCategory(uid));
      logCommunityWaypointsFiltersApplied({
        is_addition: isAddition,
        map_location: getPage(pathname),
        num_categories_selected: nextSelected.length,
        selected_categories: nextSelected.map(getWaypointCategoryValue),
        trail_id: trailId ? String(trailId) : undefined
      });
    },
    [dispatch, pathname, selected, trailId]
  );

  return (
    <div className={styles.container}>
      {COMMUNITY_WAYPOINT_FILTER_UIDS.map(uid => {
        const config = getWaypointCategoryConfig(intl, uid);
        return (
          <Checkbox
            key={uid}
            id={`community-waypoint-category-${uid}`}
            className={styles.row}
            labelElement={
              <Typography className={styles.label} color="secondary" variant="text200">
                {config.namePlural}
              </Typography>
            }
            onChange={() => onToggle(uid)}
            onClick={event => event.stopPropagation()}
            selected={selected.includes(uid)}
            inputLast
          />
        );
      })}
    </div>
  );
};

export default CommunityWaypointCategoryFilters;
